import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listUserProfiles, upsertUserProfile, getUserProfile } from '../services/profilesService';
import { listVendors, upsertVendor } from '../services/vendorsService';
import { listEvents } from '../services/eventsService';
import { listApplicationsForEvent, listBoothApplications } from '../services/applicationsService';
import { listReservedVendorIds } from '../services/reservationsService';
import { getBusinessTypes } from '../services/lookupsService';
import { userStatusOptions, userTypeOptions, vendorStatusOptions } from '../config/lookups';
import { useAuth } from '../context/AuthContext';
import EmailRecipientsModal from './EmailRecipientsModal';
import { collectUniqueEmails, toCommaDelimitedEmailText } from '../utils/emailRecipients';
import './ProfilePage.css';

// Simple admin screen to browse users and their vendors with filters
const VendorsAdmin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [viewerUserType, setViewerUserType] = useState('');
  const [eventFilterId, setEventFilterId] = useState('');
  const [appliedVendorIds, setAppliedVendorIds] = useState(null); // Set or null
  const [appliedApprovedVendorIds, setAppliedApprovedVendorIds] = useState(null); // Set or null for approved apps
  const [vendorHasApps, setVendorHasApps] = useState(null); // Set or null
  const [vendorsWithReservedBooths, setVendorsWithReservedBooths] = useState(null); // Set or null
  const [appsByEvent, setAppsByEvent] = useState(null); // Map eventId -> Set(vendorId)
  const [otherFilter, setOtherFilter] = useState('');

  const [qName, setQName] = useState(searchParams.get('name') || '');
  const [qTypes, setQTypes] = useState(() => {
    const t = searchParams.get('types');
    return t ? t.split(',').filter(Boolean) : [];
  });
  const [qEmail, setQEmail] = useState(searchParams.get('email') || '');
  const [qPhone, setQPhone] = useState(searchParams.get('phone') || '');
  const [qUserStatus, setQUserStatus] = useState(searchParams.get('status') || '');
  const [qUserType, setQUserType] = useState(searchParams.get('role') || '');
  // Pending inputs: users type into these and then click Apply to commit
  const [pendingQName, setPendingQName] = useState(qName);
  const [pendingQTypes, setPendingQTypes] = useState(qTypes);
  const [pendingQEmail, setPendingQEmail] = useState(qEmail);
  const [pendingQPhone, setPendingQPhone] = useState(qPhone);
  const [pendingQUserStatus, setPendingQUserStatus] = useState(qUserStatus);
  const [pendingQUserType, setPendingQUserType] = useState(qUserType);
  const [pendingEventFilterId, setPendingEventFilterId] = useState(eventFilterId);
  const [pendingOtherFilter, setPendingOtherFilter] = useState(otherFilter);
  // Selection and bulk update state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkUserType, setBulkUserType] = useState('');
  const [bulkUserStatus, setBulkUserStatus] = useState('');
  const [bulkVendorStatus, setBulkVendorStatus] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  const compact = true;
  const [copied, setCopied] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [modalText, setModalText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'vendorName', direction: 'asc' });
  const [filtering, setFiltering] = useState(false);
  const filterTimerRef = useRef(null);
  const searchParamsTimerRef = useRef(null);
  const { currentUser } = useAuth();

  const copyToClipboard = async (text, key) => {
    if (!text) return;
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied(prev => { const next = { ...prev }; delete next[key]; return next; }), 1500);
    } catch (e) {
      // ignore copy errors silently
    }
  };

  const openSelectedEmailsModal = () => {
    const ids = Array.from(selectedIds);
    const targetRows = ids
      .map((uid) => rows.find((r) => r.uid === uid))
      .filter(Boolean);
    const emails = collectUniqueEmails(targetRows, [
      (row) => row.vendorEmail,
      (row) => row.email,
    ]);
    setModalText(toCommaDelimitedEmailText(emails));
    setShowEmailModal(true);
  };

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value).trim();
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const exportSelectedRowsToCSV = () => {
    if (selectedIds.size === 0) return;
    
    // Get selected rows from filtered data
    const selectedRows = filtered.filter(r => selectedIds.has(r.uid));
    
    // Define CSV headers matching the visible columns
    const headers = [
      'Vendor Number',
      'Vendor Name',
      'Type',
      'Vendor Status',
      'Vendor Email',
      'Vendor Phone',
      'User Name',
      'User Email',
      'User Phone',
      'User Status',
      'User Type'
    ];
    
    // Build CSV content
    const csvLines = [headers.map(escapeCSV).join(',')];
    
    selectedRows.forEach(r => {
      const row = [
        r.vendorNumber,
        r.vendorName,
        r.vendorType,
        r.vendorStatus,
        r.vendorEmail,
        r.vendorPhone,
        r.name,
        r.email,
        r.phone,
        r.userStatus,
        r.userType
      ];
      csvLines.push(row.map(escapeCSV).join(','));
    });
    
    const csvContent = csvLines.join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `vendors-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Keep URL query params in sync with filters for persistence across navigation
  // Debounced to avoid updating the URL (and causing potential focus/navigation side-effects)
  useEffect(() => {
    if (searchParamsTimerRef.current) clearTimeout(searchParamsTimerRef.current);
    searchParamsTimerRef.current = setTimeout(() => {
      const params = new URLSearchParams();
      if (qName.trim()) params.set('name', qName.trim());
      if (qEmail.trim()) params.set('email', qEmail.trim());
      if (qPhone.trim()) params.set('phone', qPhone.trim());
      if (qUserStatus) params.set('status', qUserStatus);
      if (qUserType) params.set('role', qUserType);
      if (qTypes && qTypes.length) params.set('types', qTypes.join(','));
      setSearchParams(params, { replace: true });
      searchParamsTimerRef.current = null;
    }, 350);
    return () => {
      if (searchParamsTimerRef.current) { clearTimeout(searchParamsTimerRef.current); searchParamsTimerRef.current = null; }
    };
  }, [qName, qEmail, qPhone, qUserStatus, qUserType, qTypes, setSearchParams]);

  const clearAllFilters = () => {
    // Clear both pending inputs and active filters
    setPendingQName(''); setQName('');
    setPendingQEmail(''); setQEmail('');
    setPendingQPhone(''); setQPhone('');
    setPendingQUserStatus(''); setQUserStatus('');
    setPendingQUserType(''); setQUserType('');
    setPendingQTypes([]); setQTypes([]);
    setEventFilterId(''); setPendingEventFilterId('');
    setOtherFilter(''); setPendingOtherFilter('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const applyPendingFilters = () => {
    setQName(pendingQName);
    setQTypes(pendingQTypes || []);
    setQEmail(pendingQEmail);
    setQPhone(pendingQPhone);
    setQUserStatus(pendingQUserStatus);
    setQUserType(pendingQUserType);
    setEventFilterId(pendingEventFilterId);
    setOtherFilter(pendingOtherFilter);
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      const [p, v, bt] = await Promise.all([
        listUserProfiles(),
        listVendors(),
        getBusinessTypes(),
      ]);
      setProfiles(p || []);
      setVendors(v || []);
      setBusinessTypes(bt || []);
      setError('');
    } catch (e) {
      setError(e?.message || 'Failed to load users/vendors. Do you have admin access?');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const [p, v, bt] = await Promise.all([
          listUserProfiles(),
          listVendors(),
          getBusinessTypes(),
        ]);
        if (!mounted) return;
        setProfiles(p || []);
        setVendors(v || []);
        setBusinessTypes(bt || []);
      } catch (e) {
        setError(e?.message || 'Failed to load users/vendors. Do you have admin access?');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const vendorsById = useMemo(() => {
    const map = new Map();
    vendors.forEach(v => map.set(v.id, v));
    return map;
  }, [vendors]);

  // Load role-filtered events for the event filter (include all statuses)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await listEvents();
        let filtered = all || [];

        if (currentUser && currentUser.uid) {
          try {
            const prof = await getUserProfile(currentUser.uid);
            const userType = String(prof?.userType || '').trim();
            setViewerUserType(userType);
            const uidLower = String(currentUser.uid || '').toLowerCase();
            const nameLower = `${String(prof?.firstName || '').trim()} ${String(prof?.lastName || '').trim()}`.trim().toLowerCase();
            const emailLower = String(prof?.email || currentUser?.email || '').trim().toLowerCase();

            const isSystemAdmin = userType === 'SystemAdmin' || userType === 'Admin';
            if (isSystemAdmin) {
              // Keep full list for system admins
            } else if (userType === 'EventAdmin') {
              filtered = (all || []).filter(ev => {
                const admins = Array.isArray(ev?.eventAdmins) ? ev.eventAdmins : [];
                return admins.some(a => {
                  const val = String(a || '').trim().toLowerCase();
                  if (!val) return false;
                  return val === uidLower || (nameLower && val === nameLower) || (emailLower && val === emailLower);
                });
              });
            } else if (userType === 'Organizer') {
              filtered = (all || []).filter(ev => {
                const organizers = Array.isArray(ev?.organizers) ? ev.organizers : [];
                return organizers.some(o => String(o || '').trim().toLowerCase() === uidLower);
              });
            } else {
              filtered = [];
            }
          } catch (profileErr) {
            setViewerUserType('');
            filtered = [];
          }
        } else {
          setViewerUserType('');
          filtered = [];
        }

        if (cancelled) return;
        setEvents(filtered);
      } catch (e) {
        console.warn('Failed to load events for vendor filter', e?.message || e);
        if (!cancelled) setEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Keep pending/applied event filters valid for the currently allowed event set
  useEffect(() => {
    const allowedIds = new Set((events || []).map(ev => ev?.id).filter(Boolean));
    if (pendingEventFilterId && !allowedIds.has(pendingEventFilterId)) setPendingEventFilterId('');
    if (eventFilterId && !allowedIds.has(eventFilterId)) setEventFilterId('');
  }, [events, pendingEventFilterId, eventFilterId]);

  // Load vendor application existence map for Other Filters (once)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apps = await listBoothApplications({ limitCount: 5000 });
        if (cancelled) return;
        const ids = new Set((apps || []).map(a => a.vendorId).filter(Boolean));
        setVendorHasApps(ids);
        // Build apps-by-event map for dashboard counts
        const map = new Map();
        (apps || []).forEach(a => {
          const ev = a.eventId || a.event || '';
          if (!map.has(ev)) map.set(ev, new Set());
          if (a.vendorId) map.get(ev).add(a.vendorId);
        });
        setAppsByEvent(map);
      } catch (e) {
        console.warn('Failed to load applications for vendor-apps map', e?.message || e);
        if (!cancelled) setVendorHasApps(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load applications for selected event and build set of vendorIds
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!eventFilterId) { setAppliedVendorIds(null); return; }
      try {
        const apps = await listApplicationsForEvent(eventFilterId, { limitCount: 5000 });
        if (cancelled) return;
        const ids = new Set((apps || []).map(a => a.vendorId).filter(Boolean));
        // Also build a set of vendorIds that have an APPROVED application for this event
        const approvedIds = new Set((apps || []).filter(a => String(a.status || '').toLowerCase() === 'approved').map(a => a.vendorId).filter(Boolean));
        setAppliedVendorIds(ids);
        setAppliedApprovedVendorIds(approvedIds);
      } catch (e) {
        console.warn('Failed to load applications for event filter', e?.message || e);
        if (!cancelled) setAppliedVendorIds(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [eventFilterId]);

  // Load vendor ids that have one or more reserved booths.
  // If event is selected, scope this set to that event.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (otherFilter !== 'show-vendors-with-booths') {
        setVendorsWithReservedBooths(null);
        return;
      }
      try {
        const ids = await listReservedVendorIds({ eventId: eventFilterId || undefined });
        if (!cancelled) setVendorsWithReservedBooths(ids || new Set());
      } catch (e) {
        console.warn('Failed to load reserved-booth vendors for filter', e?.message || e);
        if (!cancelled) setVendorsWithReservedBooths(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [eventFilterId, otherFilter]);

  const rows = useMemo(() => {
    return profiles.map(p => {
      const v = p.vendorId ? vendorsById.get(p.vendorId) : null;
      return {
        uid: p.id,
        vendorId: p.vendorId || '',
        name: `${p.firstName || ''} ${p.lastName || ''}`.trim(),
        email: p.email || '',
        phone: p.phoneNumber || p.phone || '',
        userStatus: p.userStatus || '',
        userType: p.userType || '',
        vendorNumber: v?.vendorNumber || '',
        vendorName: v?.name || v?.businessName || '',
        vendorType: v?.type || v?.businessType || '',
        vendorEmail: v?.email || v?.businessEmail || '',
        vendorPhone: v?.phoneNumber || v?.phone || v?.businessPhone || '',
        vendorStatus: v?.vendorStatus || '',
      };
    });
  }, [profiles, vendorsById]);

  const vendorUserCounts = useMemo(() => {
    const counts = new Map();
    rows.forEach(r => {
      if (!r.vendorId) return;
      counts.set(r.vendorId, (counts.get(r.vendorId) || 0) + 1);
    });
    return counts;
  }, [rows]);

  const organizerAccessibleVendorIds = useMemo(() => {
    if (viewerUserType !== 'Organizer') return null;
    const allowedEventIds = new Set((events || []).map(ev => ev?.id).filter(Boolean));
    if (!appsByEvent || allowedEventIds.size === 0) return new Set();
    const ids = new Set();
    allowedEventIds.forEach(evId => {
      const setForEvent = appsByEvent.get(evId);
      if (setForEvent) setForEvent.forEach(vendorId => ids.add(vendorId));
    });
    return ids;
  }, [viewerUserType, events, appsByEvent]);

  // Normalize vendor name for duplicate detection: trim, lowercase, remove spaces
  const normalizeVendorName = (name) => {
    return (name || '').trim().toLowerCase().replace(/[\s._'-]+/g, '');
  };

  const filtered = useMemo(() => {
    const n = qName.trim().toLowerCase();
    const tSet = new Set((qTypes || []).map(t => (t || '').toLowerCase()));
    const e = qEmail.trim().toLowerCase();
    const ph = qPhone.trim().toLowerCase();
    const us = qUserStatus.trim().toLowerCase();
    const ut = qUserType.trim().toLowerCase();
    const isEventAdminWithoutAccessibleEvents = viewerUserType === 'EventAdmin' && events.length === 0;
    const isOrganizerUser = viewerUserType === 'Organizer';
    
    // For duplicate names filter: find normalized names that belong to more than one distinct vendorId
    const duplicateVendorNames = new Set();
    if (otherFilter === 'show-vendors-duplicate-names') {
      const nameToVendorIds = new Map();
      rows.forEach(r => {
        if (r.vendorName && String(r.vendorName).trim() && r.vendorId) {
          const normalized = normalizeVendorName(r.vendorName);
          if (!nameToVendorIds.has(normalized)) nameToVendorIds.set(normalized, new Set());
          nameToVendorIds.get(normalized).add(r.vendorId);
        }
      });
      // Only flag names where more than one distinct vendor ID shares the same normalized name
      nameToVendorIds.forEach((vendorIds, normalized) => {
        if (vendorIds.size > 1) {
          duplicateVendorNames.add(normalized);
        }
      });
    }
    const base = rows.filter(r => {
      // EventAdmin safety: if user has no event access, only expose rows that are
      // unassociated with any event (no business or vendor with zero applications).
      if (isEventAdminWithoutAccessibleEvents) {
        const hasBusinessRecord = Boolean(r.vendorId || (r.vendorName && String(r.vendorName).trim()));
        if (hasBusinessRecord) {
          // While application map is loading, hide business-linked rows to avoid overexposure.
          if (!vendorHasApps) return false;
          if (r.vendorId && vendorHasApps.has(r.vendorId)) return false;
        }
      }

      // Organizer safety: by default, hide vendors tied to events outside organizer scope.
      // Allow records with no business, and vendors with no applications.
      if (isOrganizerUser) {
        const hasBusinessRecord = Boolean(r.vendorId || (r.vendorName && String(r.vendorName).trim()));
        if (hasBusinessRecord) {
          if (!vendorHasApps) return false;
          const hasAnyApps = Boolean(r.vendorId && vendorHasApps.has(r.vendorId));
          if (hasAnyApps) {
            if (!organizerAccessibleVendorIds) return false;
            if (!r.vendorId || !organizerAccessibleVendorIds.has(r.vendorId)) return false;
          }
        }
      }

      const nameMatch = !n || r.name.toLowerCase().includes(n) || r.vendorName.toLowerCase().includes(n);
      const typeMatch = (tSet.size === 0) || tSet.has((r.vendorType || '').toLowerCase());
      const emailMatch = !e || (r.email.toLowerCase().includes(e) || (r.vendorEmail || '').toLowerCase().includes(e));
      const phoneMatch = !ph || (r.phone.toLowerCase().includes(ph) || (r.vendorPhone || '').toLowerCase().includes(ph));
      const userStatusMatch = !us || (r.userStatus || '').toLowerCase() === us;
      const userTypeMatch = !ut || (r.userType || '').toLowerCase() === ut;
      // If an event filter is selected, require vendor to have applied for it
      if (eventFilterId) {
        // if we couldn't load appliedVendorIds yet, exclude until loaded
        if (!appliedVendorIds) return false;
        // vendorId must be present and in the applied set
        if (!r.vendorId) return false;
        if (!appliedVendorIds.has(r.vendorId)) return false;
      }
      // Apply Other Filters if selected
      if (otherFilter) {
        if (otherFilter === 'show-users-no-business') {
          // only show rows that do NOT have an associated vendor/business
          if (r.vendorId) return false;
          if (r.vendorName && String(r.vendorName).trim()) return false;
        } else if (otherFilter === 'ignore-users-no-business') {
          // exclude rows that do NOT have an associated vendor/business
          if (!r.vendorId || !r.vendorName) return false;
        } else if (otherFilter === 'show-vendors-no-apps') {
          // only show vendor rows that have no applications
          if (!vendorHasApps) return false;
          if (!r.vendorId) return false;
          if (vendorHasApps.has(r.vendorId)) return false;
        } else if (otherFilter === 'show-vendors-with-booths') {
          // only show vendors that have one or more reserved booths
          if (!vendorsWithReservedBooths) return false;
          if (!r.vendorId) return false;
          if (!vendorsWithReservedBooths.has(r.vendorId)) return false;
          // New filter: vendors with approved applications for the selected event
        } else if (otherFilter === 'show-vendors-approved-apps') {
          // Require an event to be selected and approved set to be loaded
          if (!eventFilterId) return false;
          if (!appliedApprovedVendorIds) return false;
          if (!r.vendorId) return false;
          if (!appliedApprovedVendorIds.has(r.vendorId)) return false;
        } else if (otherFilter === 'ignore-vendors-no-apps') {
          // exclude vendor rows that have no applications
          // also exclude users that do not have any business/vendor record
          if (!vendorHasApps) return false;
          // if the row has no vendorId or vendorName, treat as user-without-business and exclude
          if (!r.vendorId || !(r.vendorName && String(r.vendorName).trim())) return false;
          // if vendor exists but has no apps, exclude
          if (!vendorHasApps.has(r.vendorId)) return false;
        } else if (otherFilter === 'show-vendors-multi-users') {
          // only show vendors that have more than one associated user profile
          if (!r.vendorId) return false;
          if ((vendorUserCounts.get(r.vendorId) || 0) <= 1) return false;
        } else if (otherFilter === 'show-vendors-duplicate-names') {
          // only show vendors where normalized vendor name matches another vendor (ignoring spaces and case)
          if (!r.vendorName || !String(r.vendorName).trim()) return false;
          const normalized = normalizeVendorName(r.vendorName);
          if (!duplicateVendorNames.has(normalized)) return false;
        }
      }
      return nameMatch && typeMatch && emailMatch && phoneMatch && userStatusMatch && userTypeMatch;
    });

    // If the approved-apps filter is selected, return unique vendor rows (dedupe by vendorId)
    if (otherFilter === 'show-vendors-approved-apps') {
      const seen = new Set();
      return base.filter(r => {
        if (!r.vendorId) return false;
        if (seen.has(r.vendorId)) return false;
        seen.add(r.vendorId);
        return true;
      });
    }

    return base;
  }, [rows, vendorUserCounts, qName, qTypes, qEmail, qPhone, qUserStatus, qUserType, eventFilterId, appliedVendorIds, appliedApprovedVendorIds, otherFilter, vendorHasApps, vendorsWithReservedBooths, viewerUserType, events.length, organizerAccessibleVendorIds, normalizeVendorName]);

  const sortedFiltered = useMemo(() => {
    const toComparable = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim().toLowerCase();
    };

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const av = toComparable(a[sortConfig.key]);
      const bv = toComparable(b[sortConfig.key]);
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return String(a.uid || '').localeCompare(String(b.uid || ''));
    });
    return sorted;
  }, [filtered, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortSuffix = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ^' : ' v';
  };

  // Reset selection when filters change
  useEffect(() => { setSelectedIds(new Set()); }, [qName, qTypes, qEmail, qPhone, qUserStatus, qUserType]);

  // Show a busy cursor while filters are being applied (debounced)
  useEffect(() => {
    // mark filtering start
    setFiltering(true);
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(() => {
      setFiltering(false);
      filterTimerRef.current = null;
    }, 250);
    return () => {
      if (filterTimerRef.current) { clearTimeout(filterTimerRef.current); filterTimerRef.current = null; }
    };
  }, [qName, qTypes, qEmail, qPhone, qUserStatus, qUserType, eventFilterId, otherFilter]);

  // Toggle document cursor while filtering
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.body.style.cursor || '';
    document.body.style.cursor = filtering ? 'wait' : '';
    return () => { document.body.style.cursor = prev; };
  }, [filtering]);

  const allFilteredIds = useMemo(() => new Set(filtered.map(r => r.uid)), [filtered]);
  const allSelectedInFilter = selectedIds.size > 0 && filtered.every(r => selectedIds.has(r.uid));
  const toggleSelectAll = () => {
    if (allSelectedInFilter) {
      // Unselect all filtered
      const next = new Set(selectedIds);
      filtered.forEach(r => next.delete(r.uid));
      setSelectedIds(next);
    } else {
      // Select all filtered
      const next = new Set(selectedIds);
      filtered.forEach(r => next.add(r.uid));
      setSelectedIds(next);
    }
  };

  const toggleRow = (uid) => {
    const next = new Set(selectedIds);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelectedIds(next);
  };

  // Dashboard computations (global, not affected by current UI filters)
  const dashboardState = useMemo(() => {
    const totalVendors = vendors ? vendors.length : 0;
    const totalUsers = profiles ? profiles.length : 0;
    const usersNoBusiness = profiles ? profiles.filter(p => !p.vendorId).length : 0;
    const vendorsNoAppsCount = (vendorHasApps === null) ? null : vendors.filter(v => !vendorHasApps.has(v.id)).length;
    const byEvent = (events || []).map(ev => ({ id: ev.id, name: ev.name || ev.id, count: (appsByEvent && appsByEvent.get(ev.id)) ? appsByEvent.get(ev.id).size : 0 }));
    const usersByStatus = {};
    (profiles || []).forEach(p => { const s = p.userStatus || 'unknown'; usersByStatus[s] = (usersByStatus[s] || 0) + 1; });
    const vendorsByStatus = {};
    (vendors || []).forEach(v => { const s = v.vendorStatus || 'unknown'; vendorsByStatus[s] = (vendorsByStatus[s] || 0) + 1; });
    return { totalVendors, totalUsers, usersNoBusiness, vendorsNoAppsCount, byEvent, usersByStatus, vendorsByStatus };
  }, [vendors, events, profiles, vendorHasApps, appsByEvent]);

  // dashboardState is passed directly to the Dashboard component via props.

  const applyBulkUpdate = async () => {
    setBulkMsg('');
    const ids = Array.from(selectedIds).filter(id => allFilteredIds.has(id));
    if (ids.length === 0) {
      setBulkMsg('Select one or more rows to update.');
      return;
    }
    if (!bulkUserType && !bulkUserStatus && !bulkVendorStatus) {
      setBulkMsg('Choose a new User Type, User Status, and/or Vendor Status to apply.');
      return;
    }
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const uid of ids) {
      try {
        const current = profiles.find(p => p.id === uid) || {};
        
        // Update user profile
        await upsertUserProfile(uid, {
          firstName: current.firstName,
          lastName: current.lastName,
          phone: current.phone,
          email: current.email,
          vendorId: current.vendorId,
          userType: bulkUserType || current.userType,
          userStatus: bulkUserStatus || current.userStatus,
          acknowledgedPolicies: current.acknowledgedPolicies,
        });
        
        // Update vendor status if applicable and vendorId exists
        if (bulkVendorStatus && current.vendorId) {
          const vendor = vendorsById.get(current.vendorId);
          if (vendor) {
            await upsertVendor(current.vendorId, {
              ...vendor,
              vendorStatus: bulkVendorStatus,
            });
          }
        }
        
        ok++;
      } catch (e) {
        fail++;
      }
    }
    setBulkBusy(false);
    setBulkMsg(`Updated ${ok} profile(s)${fail ? `, ${fail} failed` : ''}.`);
    // Refresh profiles and vendors after update
    try {
      const [p, v] = await Promise.all([
        listUserProfiles(),
        listVendors(),
      ]);
      setProfiles(p || []);
      setVendors(v || []);
    } catch {}
  };

  if (loading) return <div className="profile-container vendor-admin-container">Loading users/vendors…</div>;

  return (
  <div className="profile-container vendor-admin-container">
      {filtering && (
        <>
          <style>{`@keyframes vendorsAdminSpinnerSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}@keyframes vendorsAdminSpinnerPulse{0%{transform:scale(1);opacity:0.95}50%{transform:scale(1.06);opacity:1}100%{transform:scale(1);opacity:0.95}}`}</style>
          <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, pointerEvents: 'none' }} aria-hidden="true">
            <div style={{ width: 160, height: 160, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 28px rgba(0,0,0,0.45)', animation: 'vendorsAdminSpinnerPulse 1.6s ease-in-out infinite' }}>
              <div style={{ width: 96, height: 96, borderRadius: '50%', border: '6px solid rgba(255,255,255,0.85)', borderTopColor: '#4ECDC4', background: 'transparent', boxSizing: 'border-box', animation: 'vendorsAdminSpinnerSpin 1s linear infinite' }} />
            </div>
          </div>
        </>
      )}
      <div className="profile-header-row" style={{ gap: 12 }}>
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button 
          type="button" 
          className="apply-btn" 
          onClick={refreshData} 
          title="Refresh data without resetting filters" 
          disabled={loading}
          style={{ minWidth: 100, padding: '0.5rem 1.25rem', fontSize: '0.9rem' }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
        <button 
          type="button" 
          className="apply-btn" 
          onClick={clearAllFilters} 
          title="Clear all filters"
          style={{ minWidth: 120, padding: '0.5rem 1.25rem', fontSize: '0.9rem' }}
        >
          Reset Filters
        </button>
        <button type="button" className="cancel-btn" onClick={() => navigate('/')}>Close</button>
      </div>
      <h2 className="profile-title">Vendors Administration</h2>
      {/* Dashboard (global, not impacted by filters) */}
      <Dashboard data={dashboardState} />
      {/* Totals based on filtered rows below */}
      <div className="profile-hint" style={{ marginBottom: 12 }}>
        Total Vendors: {(() => { const s = new Set(); filtered.forEach(r => { if (r.vendorId) { s.add(`id:${r.vendorId}`); } else if ((r.vendorName || '').trim()) { s.add(`name:${r.vendorName.trim().toLowerCase()}`); } }); return s.size; })()} | Total Users: {filtered.length}
      </div>
      {error && <div className="profile-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="filters-panel" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Filters</div>
            <div className="profile-hint" style={{ margin: 0 }}>Filters change which rows are shown.</div>
          </div>
          <div style={{ color: '#666', fontSize: 13 }}>
            <button type="button" className="apply-btn" onClick={applyPendingFilters} style={{ padding: '6px 10px', marginRight: 8 }}>Apply Filters</button>
            <button type="button" className="apply-btn" onClick={clearAllFilters} style={{ padding: '6px 10px', minWidth: 120 }}>Reset Filters</button>
            
          </div>
        </div>
        <div className="profile-grid">
          <div>
            <label className="profile-label">Select Event</label>
            <select value={pendingEventFilterId} onChange={e => setPendingEventFilterId(e.target.value)} className="profile-input" disabled={filtering} title={filtering ? 'Filtering in progress' : ''}>
              {(viewerUserType === 'SystemAdmin' || viewerUserType === 'Admin') && <option value="">— All events —</option>}
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>{ev.name || ev.id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="profile-label">Name or Vendor Name</label>
            <input value={pendingQName} onChange={e => setPendingQName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyPendingFilters(); }} className="profile-input" placeholder="e.g. Jane or Acme Co." disabled={filtering} title={filtering ? 'Filtering in progress' : ''} />
          </div>
          <div>
            <label className="profile-label">Business Type (multi-select)</label>
            <select
              multiple
              value={pendingQTypes}
              onChange={e => setPendingQTypes(Array.from(e.target.selectedOptions).map(o => o.value))}
              className="profile-input"
              size={Math.min(6, Math.max(3, businessTypes.length))}
              disabled={filtering}
              title={filtering ? 'Filtering in progress' : ''}
            >
              {businessTypes.map(bt => (
                <option key={bt} value={bt}>{bt}</option>
              ))}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
              <div className="profile-hint">Hold Ctrl (Windows) or Cmd (Mac) to select multiple types. Leave empty for all.</div>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setQTypes([])}
                disabled={!qTypes.length || filtering}
                title={filtering ? 'Filtering in progress' : 'Unselect all business types'}
                style={{ padding: '4px 10px' }}
              >Clear Types</button>
            </div>
          </div>
          <div>
            <label className="profile-label">Email (User or Vendor)</label>
            <input value={pendingQEmail} onChange={e => setPendingQEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyPendingFilters(); }} className="profile-input" placeholder="e.g. user@ex.com or biz@ex.com" disabled={filtering} title={filtering ? 'Filtering in progress' : ''} />
          </div>
          <div>
            <label className="profile-label">Phone (User or Vendor)</label>
            <input value={pendingQPhone} onChange={e => setPendingQPhone(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyPendingFilters(); }} className="profile-input" placeholder="Any part of number" disabled={filtering} title={filtering ? 'Filtering in progress' : ''} />
          </div>
          <div>
            <label className="profile-label">User Status</label>
            <select value={pendingQUserStatus} onChange={e => setPendingQUserStatus(e.target.value)} className="profile-input" disabled={filtering} title={filtering ? 'Filtering in progress' : ''}>
              <option value="">All statuses</option>
              {userStatusOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="profile-label">User Type</label>
            <select value={pendingQUserType} onChange={e => setPendingQUserType(e.target.value)} className="profile-input" disabled={filtering} title={filtering ? 'Filtering in progress' : ''}>
              <option value="">All types</option>
              {userTypeOptions.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="profile-label">Other Filters</label>
            <select value={pendingOtherFilter} onChange={e => setPendingOtherFilter(e.target.value)} className="profile-input" disabled={filtering} title={filtering ? 'Filtering in progress' : ''}>
              <option value="">— None —</option>
              <option value="show-users-no-business">Users with no Business Records</option>
              <option value="ignore-users-no-business">Ignore Users with no Business Records</option>
              <option value="show-vendors-no-apps">Vendors with no Applications</option>
              <option value="show-vendors-with-booths">Vendors with one or more booths</option>
              <option value="show-vendors-approved-apps">Vendors with Approved Applications</option>
              <option value="show-vendors-multi-users">Vendors with Multiple Users</option>
              <option value="show-vendors-duplicate-names">Show Vendors with Duplicate Names</option>
              <option value="ignore-vendors-no-apps">Ignore Vendors with no Applications</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk update controls */}
      <div className="profile-grid" style={{ marginBottom: 12 }}>
          <div className="bulk-panel" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 700 }}>Bulk Actions</div>
                <div className="profile-hint" style={{ margin: 0 }}>Bulk actions apply only to selected rows.</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="bulk-badge" style={{ padding: '4px 8px', borderRadius: 999, background: '#4ECDC4', color: '#fff', fontWeight: 700 }}>{selectedIds.size} selected</div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label className="profile-label">Bulk User Type</label>
                  <select value={bulkUserType} onChange={e => setBulkUserType(e.target.value)} className="profile-input" disabled={selectedIds.size === 0} title={selectedIds.size === 0 ? 'Select rows to enable' : ''}>
                    <option value="">— No change —</option>
                    {userTypeOptions.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="profile-label">Bulk User Status</label>
                  <select value={bulkUserStatus} onChange={e => setBulkUserStatus(e.target.value)} className="profile-input" disabled={selectedIds.size === 0} title={selectedIds.size === 0 ? 'Select rows to enable' : ''}>
                    <option value="">— No change —</option>
                    {userStatusOptions.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="profile-label">Bulk Vendor Status</label>
                  <select value={bulkVendorStatus} onChange={e => setBulkVendorStatus(e.target.value)} className="profile-input" disabled={selectedIds.size === 0} title={selectedIds.size === 0 ? 'Select rows to enable' : ''}>
                    <option value="">— No change —</option>
                    {vendorStatusOptions.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
                <button
                  type="button"
                  className="cancel-btn"
                  disabled={bulkBusy}
                  onClick={toggleSelectAll}
                  title={allSelectedInFilter ? 'Unselect all filtered' : 'Select all filtered'}
                >
                  {allSelectedInFilter ? 'Unselect All (Filtered)' : 'Select All (Filtered)'}
                </button>
                <button
                  type="button"
                  className="apply-btn"
                  disabled={bulkBusy || selectedIds.size === 0}
                  onClick={applyBulkUpdate}
                  title={selectedIds.size === 0 ? 'Select rows to enable' : 'Apply changes to selected profiles'}
                >
                  {bulkBusy ? 'Updating…' : `Apply to ${selectedIds.size} selected`}
                </button>
                <button
                  type="button"
                  className="apply-btn"
                  disabled={selectedIds.size === 0}
                  onClick={openSelectedEmailsModal}
                  title={selectedIds.size === 0 ? 'Select rows to enable' : 'Copy selected vendor/user emails'}
                  style={{ marginLeft: 8 }}
                >
                  Copy Selected Email IDs
                </button>
                <button
                  type="button"
                  className="apply-btn"
                  disabled={selectedIds.size === 0}
                  onClick={exportSelectedRowsToCSV}
                  title={selectedIds.size === 0 ? 'Select rows to enable' : 'Export selected rows as CSV'}
                  style={{ marginLeft: 8 }}
                >
                  Download CSV
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  disabled={bulkBusy || selectedIds.size === 0}
                  onClick={() => setSelectedIds(new Set())}
                  title={selectedIds.size === 0 ? 'Select rows to enable' : 'Clear current selection'}
                >
                  Clear Selection
                </button>
              </div>
              <div className="profile-hint" style={{ marginTop: 8 }}>
                Selected {selectedIds.size} of {filtered.length} filtered.
                {bulkMsg ? ` ${bulkMsg}` : ''}
              </div>
            </div>
          </div>
      </div>

      <EmailRecipientsModal
        open={showEmailModal}
        title="Selected Email IDs"
        subtitle="Edit the comma-delimited list, then click Copy to copy to clipboard."
        initialValue={modalText}
        onClose={() => setShowEmailModal(false)}
      />

      <div style={{ overflowX: 'auto' }}>
        <table className={`profile-table ${compact ? 'compact' : ''}`} style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '8px', width: 36 }}>
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && allSelectedInFilter}
                  onChange={toggleSelectAll}
                  aria-label="Select all filtered"
                />
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('vendorNumber')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  Vendor Number{sortSuffix('vendorNumber')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('vendorName')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  Vendor{sortSuffix('vendorName')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('vendorType')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  Type{sortSuffix('vendorType')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('vendorStatus')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  Vendor Status{sortSuffix('vendorStatus')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('vendorEmail')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  Vendor Email{sortSuffix('vendorEmail')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('vendorPhone')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  Vendor Phone{sortSuffix('vendorPhone')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('name')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  User Name{sortSuffix('name')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('email')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  User Email{sortSuffix('email')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('phone')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  User Phone{sortSuffix('phone')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('userStatus')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  User Status{sortSuffix('userStatus')}
                </button>
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>
                <button type="button" onClick={() => requestSort('userType')} style={{ border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                  User Type{sortSuffix('userType')}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedFiltered.map(r => (
              <tr key={r.uid}>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.uid)}
                    onChange={() => toggleRow(r.uid)}
                    aria-label={`Select ${r.name || r.vendorName || r.uid}`}
                  />
                </td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                  {r.vendorNumber}
                </td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                  {r.vendorName ? (
                    <a
                      href={`/profile/${r.uid}?src=admin`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/profile/${r.uid}?src=admin`, { state: { fromAdmin: true } });
                      }}
                      style={{ 
                        color: '#0066cc', 
                        textDecoration: 'underline', 
                        cursor: 'pointer' 
                      }}
                    >
                      {r.vendorName}
                    </a>
                  ) : ''}
                </td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.vendorType}</td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.vendorStatus}</td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{r.vendorEmail}</span>
                  {r.vendorEmail && (
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(r.vendorEmail, `${r.uid}-vendor`); }}
                      title="Copy vendor email"
                      aria-label="Copy vendor email"
                    >
                      {copied[`${r.uid}-vendor`] ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'block' }}>
                          <path fill="#4ECDC4" d="M9 3h11a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                          <path fill="#fff" d="M6 7h11v12H6z" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'block' }}>
                          <rect x="9" y="4" width="11" height="14" rx="2" stroke="#666" strokeWidth="1.5" />
                          <rect x="4" y="8" width="11" height="12" rx="2" stroke="#666" strokeWidth="1.5" />
                        </svg>
                      )}
                    </button>
                  )}
                </td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.vendorPhone}</td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                  <a
                    href={`/profile/${r.uid}?src=admin`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/profile/${r.uid}?src=admin`, { state: { fromAdmin: true } });
                    }}
                    style={{
                      color: '#0066cc',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    {r.name || '(no name)'}
                  </a>
                </td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{r.email}</span>
                  {r.email && (
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(r.email, `${r.uid}-user`); }}
                      title="Copy user email"
                      aria-label="Copy user email"
                    >
                      {copied[`${r.uid}-user`] ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'block' }}>
                          <path fill="#4ECDC4" d="M9 3h11a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                          <path fill="#fff" d="M6 7h11v12H6z" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'block' }}>
                          <rect x="9" y="4" width="11" height="14" rx="2" stroke="#666" strokeWidth="1.5" />
                          <rect x="4" y="8" width="11" height="12" rx="2" stroke="#666" strokeWidth="1.5" />
                        </svg>
                      )}
                    </button>
                  )}
                </td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.phone}</td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.userStatus}</td>
                <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{r.userType}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: '12px', textAlign: 'center', color: '#666' }}>No results</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VendorsAdmin;

// Small dashboard component placed here to keep file self-contained.
function Dashboard({ data }) {
  if (!data) {
    return (
      <div className="profile-dashboard" style={{ marginBottom: 12, padding: 12, borderRadius: 8, background: '#f7f9fb', display: 'flex', gap: 16, alignItems: 'center' }}>
        <div style={{ color: '#666' }}>Loading dashboard…</div>
      </div>
    );
  }
  const { totalVendors, totalUsers, usersNoBusiness, vendorsNoAppsCount, byEvent, usersByStatus, vendorsByStatus } = data;
  return (
    <div className="profile-dashboard" style={{ marginBottom: 12, padding: 12, borderRadius: 8, background: '#f7f9fb' }}>
      {/* First row: User metrics */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 13, color: '#666' }}>Total Users</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{totalUsers ?? '—'}</div>
        </div>
        <div style={{ minWidth: 220 }}>
          <div style={{ fontSize: 13, color: '#666' }}>Users by Status</div>
          <div style={{ marginTop: 6 }}>
            {usersByStatus && Object.keys(usersByStatus).length ? Object.entries(usersByStatus).map(([k,v]) => (
              <div key={k} style={{ fontSize: 13, color: '#333' }}>{k}: {v}</div>
            )) : <div style={{ color: '#999' }}>No user status data</div>}
          </div>
        </div>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 13, color: '#666' }}>Users w/o Business Records</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{usersNoBusiness ?? '—'}</div>
        </div>
      </div>

      {/* Second row: Vendor metrics */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 13, color: '#666' }}>Total Vendors</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{totalVendors ?? '—'}</div>
        </div>
        <div style={{ minWidth: 220 }}>
          <div style={{ fontSize: 13, color: '#666' }}>Vendors by Status</div>
          <div style={{ marginTop: 6 }}>
            {vendorsByStatus && Object.keys(vendorsByStatus).length ? Object.entries(vendorsByStatus).map(([k,v]) => (
              <div key={k} style={{ fontSize: 13, color: '#333' }}>{k}: {v}</div>
            )) : <div style={{ color: '#999' }}>No vendor status data</div>}
          </div>
        </div>
        <div style={{ minWidth: 220 }}>
          <div style={{ fontSize: 13, color: '#666' }}>Vendors by Event</div>
          <div style={{ marginTop: 6 }}>
            {byEvent && byEvent.length ? byEvent.map(ev => (
              <div key={ev.id} style={{ fontSize: 13, color: '#333' }}>{ev.name || ev.id}: {ev.count}</div>
            )) : <div style={{ color: '#999' }}>No event data</div>}
          </div>
        </div>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontSize: 13, color: '#666' }}>Vendors w/o Applications</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{vendorsNoAppsCount ?? '—'}</div>
        </div>
      </div>
    </div>
  );
}
